import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"
import styled from "styled-components"

import {
    PENDING, IDLE, EDIT, YES_NO, NUMBER, TEXT, PUBLISHED, UNPUBLISHED,
} from "const"
import { RrsCategory, RrsCategoryCreate } from "server/models"
import {
    StateType,
    RrsCategoryCustomVariableType,
    RrsCategoryCustomVariableValue,
    RrsCategoryCustomVariable,
    forceRerenderContent,
    replaceHistoryState,
    useInput,
} from "client/utils"
import {
    StoreType,
    LangType,
    RrsCategoryValidation,
    RrsCategoryCustomVariableValidation,

    SaveRrsCategory,
    SortRrsCategories,
    PrepareRrsCategoryNestedIds,

    setRrsCategoryEdit,
    setRrsCategoryReloadList,
    setRrsCategoryReloadEdit,
    closeModal,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Textarea,
    DivInput,
    SelectDropdown,
    SelectDropdownItem,
    SelectDropdownSearch,
    RadioButton,
    Delimiter,
    Feedback,
    Empty,
    Button,
    SpinnerIcon,
    TableNestedIds,
} from "client/components"


const CustomVariableRow = styled.div`
    display: flex;

    & > div {
        margin-right: 5px;
        margin-left: 5px;

        &:first-child {
            margin-left: 0;
        }

        &:last-child {
            margin-right: 0;
        }
    }

    & > ${Button} {
        align-self: flex-start;
        margin-top: 18px;
    }
`

const RadioButtonWrapper = styled.div`
    display: flex;
    height: 33px;
`


let isMounted: boolean

interface EditRrsCategoryProps {
    category: RrsCategory
}

const Form = ({
    category,
}: EditRrsCategoryProps) => {
    const baseRoute = useSelector((state: StoreType) => state.rrsCategory.baseRoute)
    const categoryList = useSelector((state: StoreType) => state.rrsCategory.categoryList)
    const childParent = useSelector((state: StoreType) => state.rrsCategory.categoryChildParent)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [nestedCategoryList, setNestedCategoryList] = useState<any[]>([])
    const [parentCategory, setParentCategory] = useState<string>(null)
    const [parentCategorySearch, setParentCategorySearch] = useState<string>("")

    const [categoryId, setCategoryId] = useState(category.categoryId)
    const [name, setName] = useInput(category.name)
    const [shortName, setShortName] = useInput(category.shortName)
    const [description, setDescription] = useInput(category.description)
    const [order, setOrder] = useState(category.order)
    const [status, setStatus] = useState(category.status)
    const [customVariables, setCustomVariables] = useState<readonly RrsCategoryCustomVariable[]>(category.customVariablesParsed || [])

    const [validation, setValidation] = useState<RrsCategoryValidation>(null)
    const [customVariablesValidation, setCustomVariablesValidation] = useState<RrsCategoryCustomVariableValidation>(null)
    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        const categoriesFiltered = SortRrsCategories(Object.values(categoryList), { search: parentCategorySearch })
        const groupedIds = PrepareRrsCategoryNestedIds(categoriesFiltered)

        let parentArray: string[]
        const addParent = (parentId: number) => {
            if (parentId) {
                const parent = categoryList[parentId]
                if (parent) parentArray.unshift(parent.shortName.length ? parent.shortName : parent.name)
                addParent(childParent[parentId])
            }
        }

        const nestedList: any[] = []
        const addNested = (nestedIds: readonly TableNestedIds[], level: number) => {
            nestedIds.forEach((row) => {
                const id = Number(row.id)
                if (id === category.id) return

                const categoryItem = categoryList[id]
                let rowName: string

                if (parentCategorySearch.length) {
                    parentArray = []
                    addParent(categoryItem.id)
                    rowName = parentArray.join(" > ") // as minimum there will be searched category
                } else if (categoryItem.shortName.length) rowName = `${categoryItem.shortName} (${categoryItem.name})`
                else rowName = categoryItem.name

                nestedList.push(
                    <SelectDropdownItem
                        key={id}
                        onClick={() => setCategoryId(id)}
                    >
                        <span style={{ paddingLeft: `${level * 30}px` }}>{rowName}</span>
                    </SelectDropdownItem>,
                )
                addNested(row.nestedIds, level + 1)
            })
        }
        if (groupedIds[0]) addNested(groupedIds[0].nestedIds, 0) // where groupedIds[0] - top level group

        setNestedCategoryList(nestedList)
    }, [categoryList, childParent, parentCategorySearch])

    useEffect(() => {
        const parentArray: string[] = []
        const addParent = (parentId: number) => {
            if (parentId) {
                const parent = categoryList[parentId]
                if (parent) parentArray.unshift(parent.shortName.length ? parent.shortName : parent.name)
                addParent(childParent[parentId])
            }
        }
        addParent(categoryId)
        setParentCategory(parentArray.length ? parentArray.join(" > ") : null)
    }, [categoryId, categoryList, childParent])

    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            if (isMounted) dispatch(closeModal())
            return
        }

        forceRerenderContent(baseRoute, dispatch)
    }

    const onAddCustomVariable = () => {
        const newCustomVariable: RrsCategoryCustomVariable = {
            index: Date.now(),
            name: "",
            type: YES_NO,
            value: false,
        }
        const customVariablesCopy = customVariables.slice()
        customVariablesCopy.push(newCustomVariable)
        setCustomVariables(customVariablesCopy)
    }

    const onDeleteCustomVariable = (index: number) => {
        const customVariablesFiltered = customVariables.filter((row) => row.index !== index)
        setCustomVariables(customVariablesFiltered)
    }

    const onChangeCustomName = (index: number, value: string) => {
        const customVariablesCopy = customVariables.slice()
        customVariablesCopy[index] = {
            ...customVariablesCopy[index],
            name: value,
        }
        setCustomVariables(customVariablesCopy)
    }

    const onChangeCustomType = (index: number, type: RrsCategoryCustomVariableType, value: RrsCategoryCustomVariableValue) => {
        const customVariablesCopy = customVariables.slice()
        customVariablesCopy[index] = {
            ...customVariablesCopy[index],
            type,
            value,
        }
        setCustomVariables(customVariablesCopy)
    }

    const onChangeCustomValue = (index: number, value: RrsCategoryCustomVariableValue) => {
        const customVariablesCopy = customVariables.slice()
        customVariablesCopy[index] = {
            ...customVariablesCopy[index],
            value,
        }
        setCustomVariables(customVariablesCopy)
    }

    const onSave = (mode: number) => {
        const customValidation: RrsCategoryCustomVariableValidation = {}
        customVariables.forEach((custom) => {
            const validationName = !custom.name.trim().length ? lang.validationFieldRequired : null
            let validationValue: string = null
            if (
                (custom.type === NUMBER && !custom.value)
                || (custom.type === TEXT && !(custom.value as string).trim().length)
            ) validationValue = lang.validationFieldRequired

            if (validationName || validationValue) {
                customValidation[custom.index] = {
                    name: validationName,
                    value: validationValue,
                }
            }
        })

        if (Object.keys(customValidation).length) {
            setCustomVariablesValidation(customValidation)
            return
        }
        setCustomVariablesValidation(null)


        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newCategoryData: RrsCategoryCreate = {
            originId: category.originId || null,
            accountId: category.accountId || userData.accountId,
            projectId: category.projectId || project.id,
            categoryId,
            name: name.trim(),
            shortName: shortName.trim(),
            description: description.trim(),
            order,
            status,
            customVariables: JSON.stringify(customVariables),
        }

        SaveRrsCategory(
            category.id,
            newCategoryData,
            lang,
        ).then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)

                if (res.newCategoryId) replaceHistoryState(`${baseRoute}/${EDIT}/${res.newCategoryId}`)
            }
            if (res.validation) return

            dispatch(setRrsCategoryEdit({
                id: res.newCategoryId || category.id,
                ...newCategoryData,
                customVariablesParsed: customVariables,
            }))

            dispatch(setRrsCategoryReloadList(false))
            setTimeout(() => {
                dispatch(setRrsCategoryReloadList(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setRrsCategoryReloadEdit(false))
                setTimeout(() => {
                    dispatch(setRrsCategoryReloadEdit(true))
                }, 0)
            }
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.shortName}</Label>
                        <Input type="text" value={shortName} onChange={setShortName} />
                        <Feedback>{validation && validation.shortName}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.description}</Label>
                        <Textarea value={description} onChange={setDescription} />
                        <Feedback>{validation && validation.description}</Feedback>
                    </FormGroup>
                    {
                        // don't allow changing parent for existing category, allow only for Super Admin
                        category.id && userData.accountId !== 1
                            ? (
                                <FormGroup>
                                    <Label>{lang.parentCategory}</Label>
                                    <DivInput widePadding>{parentCategory || <Empty />}</DivInput>
                                </FormGroup>
                            )
                            : (
                                <FormGroup>
                                    <Label>{lang.parentCategory}</Label>
                                    <SelectDropdown title={parentCategory || <Empty />}>
                                        <SelectDropdownSearch
                                            value={parentCategorySearch}
                                            onChange={(e) => setParentCategorySearch(e.target.value)}
                                            onClearSearch={() => setParentCategorySearch("")}
                                        />
                                        <SelectDropdownItem onClick={() => setCategoryId(null)}>
                                            <Empty />
                                        </SelectDropdownItem>
                                        { nestedCategoryList }
                                    </SelectDropdown>
                                    <Feedback>{validation && validation.categoryId}</Feedback>
                                </FormGroup>
                            )
                    }
                    <FormGroup>
                        <Label>{lang.order}</Label>
                        <Input
                            type="text"
                            value={order !== null ? order : ""}
                            onChange={(e) => {
                                const parsed = parseInt(e.target.value, 10)
                                if (!Number.isNaN(parsed)) setOrder(parsed)
                                else setOrder(null)
                            }}
                        />
                        <Feedback>{validation && validation.order}</Feedback>
                    </FormGroup>
                    {
                        userData.accountId === 1 && categoryId === null && (
                            <FormGroup>
                                <Label>{lang.status}</Label>
                                <SelectDropdown title={lang[status as keyof LangType]}>
                                    <SelectDropdownItem onClick={() => setStatus(PUBLISHED)}>{lang.published}</SelectDropdownItem>
                                    <SelectDropdownItem onClick={() => setStatus(UNPUBLISHED)}>{lang.unpublished}</SelectDropdownItem>
                                </SelectDropdown>
                            </FormGroup>
                        )
                    }

                    <Delimiter size="sm" />

                    <FormGroup>
                        <Label>{lang.customVariables}</Label>
                        {
                            customVariables.map((custom, index) => {
                                let typeString: string = ""
                                if (custom.type === YES_NO) typeString = lang.yes_no
                                if (custom.type === NUMBER) typeString = lang.number
                                if (custom.type === TEXT) typeString = lang.text

                                const customValidation = customVariablesValidation && customVariablesValidation[custom.index]

                                return (
                                    <CustomVariableRow key={custom.index}>
                                        <FormGroup>
                                            <Label>{lang.name}</Label>
                                            <Input
                                                type="text"
                                                value={custom.name}
                                                onChange={(e) => onChangeCustomName(index, e.target.value)}
                                            />
                                            <Feedback>{customValidation && customValidation.name}</Feedback>
                                        </FormGroup>
                                        <FormGroup>
                                            <Label>{lang.type}</Label>
                                            <SelectDropdown title={typeString}>
                                                <SelectDropdownItem onClick={() => onChangeCustomType(index, YES_NO, false)}>
                                                    {lang.yes_no}
                                                </SelectDropdownItem>
                                                <SelectDropdownItem onClick={() => onChangeCustomType(index, NUMBER, 0)}>
                                                    {lang.number}
                                                </SelectDropdownItem>
                                                <SelectDropdownItem onClick={() => onChangeCustomType(index, TEXT, "")}>
                                                    {lang.text}
                                                </SelectDropdownItem>
                                            </SelectDropdown>
                                        </FormGroup>
                                        <FormGroup>
                                            <Label>{lang.value}</Label>
                                            {
                                                custom.type === YES_NO && ( // custom.value is boolean
                                                    <RadioButtonWrapper>
                                                        <RadioButton
                                                            checked={custom.value as boolean}
                                                            onChange={() => onChangeCustomValue(index, true)}
                                                            title={lang.yes}
                                                        />
                                                        <RadioButton
                                                            checked={!(custom.value as boolean)}
                                                            onChange={() => onChangeCustomValue(index, false)}
                                                            title={lang.no}
                                                        />
                                                    </RadioButtonWrapper>
                                                )
                                            }
                                            {
                                                custom.type === NUMBER && (
                                                    <Input
                                                        type="text"
                                                        value={custom.value !== null ? custom.value.toString() : ""}
                                                        onChange={(e) => {
                                                            const parsed = parseInt(e.target.value, 10)
                                                            if (!Number.isNaN(parsed)) onChangeCustomValue(index, parsed)
                                                            else onChangeCustomValue(index, null)
                                                        }}
                                                    />
                                                )
                                            }
                                            {
                                                custom.type === TEXT && (
                                                    <Input
                                                        type="text"
                                                        value={custom.value as string}
                                                        onChange={(e) => onChangeCustomValue(index, e.target.value)}
                                                    />
                                                )
                                            }
                                            <Feedback>{customValidation && customValidation.value}</Feedback>
                                        </FormGroup>
                                        <Button onClick={() => onDeleteCustomVariable(custom.index)}>{lang.delete}</Button>
                                    </CustomVariableRow>
                                )
                            })
                        }
                        <Button onClick={onAddCustomVariable}>{lang.add}</Button>
                    </FormGroup>

                    <Delimiter size="sm" />
                </Col>
                <Col xs={12}>
                    <Button
                        variant="success"
                        row
                        onClick={() => onSave(1)}
                    >
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button
                        variant="success"
                        row
                        onClick={() => onSave(2)}
                    >
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
